/**
 * DateRangePicker — a start/end date range in an anchored popover: quick presets
 * down the side, two native `<input type="date">` fields, Clear/Done in the
 * footer. Value is `{ start, end }` as ISO (YYYY-MM-DD) strings.
 *
 * Deliberately native inputs rather than a bespoke calendar — the OS picker is
 * faster on a keyboard and needs no extra surface, matching the kit's native
 * `Select`. Picking a start clears a stale earlier end; an end before the start
 * swaps them, so the range is always ordered.
 */
import { For, type JSX } from "solid-js";
import CalendarRange from "lucide-solid/icons/calendar-range";
import { Popover } from "./popover";
import { Button } from "./button";
import { cn } from "./utils/cn";
import { focusRing } from "./utils/focus-ring";

export interface DateRange {
  start?: string;
  end?: string;
}

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const toISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const shift = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toISO(d);
};

export function formatDate(iso?: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const fieldInput = cn(
  "h-8 w-full min-w-0 rounded-small border border-[color:var(--c9-border-input)] bg-[var(--c9-bg-input)]",
  "px-100 text-body text-[color:var(--c9-text)] hover:border-[color:var(--c9-border-bold)]",
);

const triggerBase = cn(
  "inline-flex h-8 w-full min-w-0 items-center justify-between gap-100 rounded-small",
  "border border-[color:var(--c9-border-input)] bg-[var(--c9-bg-input)] px-100 text-body text-[color:var(--c9-text)]",
  "hover:border-[color:var(--c9-border-bold)]",
);

export function DateRangePicker(props: {
  value: DateRange;
  onChange: (value: DateRange) => void;
  placeholder?: string;
  label?: string;
  class?: string;
  width?: number;
}): JSX.Element {
  const label = () => {
    const { start, end } = props.value;
    if (start && end) return `${formatDate(start)} – ${formatDate(end)}`;
    if (start) return `${formatDate(start)} – …`;
    if (end) return `… – ${formatDate(end)}`;
    return props.placeholder ?? "Select range";
  };
  const has = () => !!(props.value.start || props.value.end);

  const presets = [
    { label: "Today", get: (): DateRange => ({ start: shift(0), end: shift(0) }) },
    { label: "Last 7 days", get: (): DateRange => ({ start: shift(-6), end: shift(0) }) },
    { label: "Last 30 days", get: (): DateRange => ({ start: shift(-29), end: shift(0) }) },
    { label: "Last 90 days", get: (): DateRange => ({ start: shift(-89), end: shift(0) }) },
  ];

  const pickStart = (iso: string) => {
    const start = iso || undefined;
    // Drop a stale end that now precedes the new start.
    const end = start && props.value.end && props.value.end < start ? undefined : props.value.end;
    props.onChange({ start, end });
  };
  const pickEnd = (iso: string) => {
    const end = iso || undefined;
    // An end before the start swaps them, so the range stays ordered.
    if (props.value.start && end && end < props.value.start)
      props.onChange({ start: end, end: props.value.start });
    else props.onChange({ start: props.value.start, end });
  };

  return (
    <Popover
      width={props.width ?? 320}
      trigger={({ toggle, open, setRef }) => (
        <button
          ref={setRef}
          type="button"
          onClick={toggle}
          aria-haspopup="dialog"
          aria-expanded={open()}
          aria-label={props.label}
          class={cn(triggerBase, focusRing, props.class)}
        >
          <span class={cn("min-w-0 truncate", !has() && "text-[color:var(--c9-text-subtlest)]")}>{label()}</span>
          <CalendarRange size={16} class="shrink-0 text-[color:var(--c9-icon-subtle)]" />
        </button>
      )}
    >
      {({ close }) => (
        <>
          <div class="min-h-0 flex-1 overflow-y-auto">
            <div class="flex flex-col gap-025 border-b border-[color:var(--c9-border)] p-100">
              <For each={presets}>
                {(p) => (
                  <button
                    type="button"
                    onClick={() => props.onChange(p.get())}
                    class={cn(
                      "rounded-small px-100 py-075 text-left text-body text-[color:var(--c9-text)] hover:bg-[var(--c9-bg-neutral-subtle-hovered)]",
                      focusRing,
                    )}
                  >
                    {p.label}
                  </button>
                )}
              </For>
            </div>
            <div class="flex flex-col gap-150 p-150">
              <label class="flex flex-col gap-050">
                <span class="text-body-s font-[630] text-[color:var(--c9-text-subtle)]">Start</span>
                <input
                  type="date"
                  class={cn(fieldInput, focusRing)}
                  value={props.value.start ?? ""}
                  max={props.value.end || undefined}
                  onInput={(e) => pickStart(e.currentTarget.value)}
                />
              </label>
              <label class="flex flex-col gap-050">
                <span class="text-body-s font-[630] text-[color:var(--c9-text-subtle)]">End</span>
                <input
                  type="date"
                  class={cn(fieldInput, focusRing)}
                  value={props.value.end ?? ""}
                  min={props.value.start || undefined}
                  onInput={(e) => pickEnd(e.currentTarget.value)}
                />
              </label>
            </div>
          </div>
          <div class="flex shrink-0 items-center justify-between border-t border-[color:var(--c9-border)] p-100">
            <Button appearance="subtle" size="compact" onClick={() => props.onChange({})}>
              Clear
            </Button>
            <Button appearance="primary" size="compact" onClick={close}>
              Done
            </Button>
          </div>
        </>
      )}
    </Popover>
  );
}
